// src/pages/workloads/FollowUpRoundsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  RefreshCcw,
  Plus,
  Calendar,
  Filter,
  Clock,
  MapPin,
  Users,
  CheckCircle,
  AlertCircle,
  X,
  ChevronRight,
  FileText,
  Target
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { workloadService } from '../../services/workload.service';
import { Workload, WorkloadProgress } from '../../types/workload.types';
import { format, subDays, parseISO } from 'date-fns';
import { toast } from 'react-hot-toast';

interface FollowUpRound {
  id: string;
  name: string;
  sourceWorkloadId: string;
  sourceWorkloadName: string;
  projectId: string;
  createdAt: Date;
  filters: FollowUpFilters;
  addressCount: number;
  status: 'draft' | 'active' | 'completed';
}

interface FollowUpFilters {
  dispositions: string[];
  daysSinceLastVisit: number;
  maxAttempts: number;
  excludeConverted: boolean;
}

const FollowUpRoundsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { selectedProject, projects } = useProject();
  const [workloads, setWorkloads] = useState<Workload[]>([]);
  const [followUpRounds, setFollowUpRounds] = useState<FollowUpRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = userProfile?.role === 'provider_admin' || userProfile?.role === 'client_admin';

  useEffect(() => {
    loadData();
  }, [selectedProject]);

  const loadData = async () => {
    if (!selectedProject) {
      setLoading(false);
      return;
    }

    try {
      const workloadList = await workloadService.getWorkloads({
        projectId: selectedProject.id,
        status: ['active', 'completed']
      });
      setWorkloads(workloadList);

      // In a real implementation, follow-up rounds would be stored in Firestore
      // For now, we'll use an empty array
      setFollowUpRounds([]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedProject) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <RefreshCcw className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Project</h3>
          <p className="text-gray-500">Please select a project to manage follow-up rounds.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Follow-Up Rounds</h1>
          <p className="text-gray-500 mt-1">Create targeted follow-up workloads from previous rounds</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200"
          >
            <Plus className="h-5 w-5" />
            Create Follow-Up Round
          </button>
        )}
      </div>

      {/* Source Workloads */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Available Source Workloads</h3>
          <p className="text-sm text-gray-500 mt-1">Select a workload to create follow-up rounds from</p>
        </div>

        {workloads.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No workloads found</h3>
            <p className="text-gray-500">Create workloads first to enable follow-up rounds.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {workloads.map((workload) => (
              <div
                key={workload.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      workload.status === 'completed'
                        ? 'bg-accent-100 text-accent-600'
                        : 'bg-secondary-100 text-secondary-600'
                    }`}>
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">{workload.name}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {workload.progress.totalAddresses} addresses
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {workload.progress.completed} completed
                        </span>
                        <span className="flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {workload.progress.notHome} not home
                        </span>
                        <span className="flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          {workload.progress.leadsGenerated} leads
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-1 px-3 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
                  >
                    Create Follow-Up
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Follow-Up Rounds */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Follow-Up Rounds</h3>
        </div>

        {followUpRounds.length === 0 ? (
          <div className="p-12 text-center">
            <RefreshCcw className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No follow-up rounds yet</h3>
            <p className="text-gray-500">Create a follow-up round from a completed workload.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {followUpRounds.map((round) => (
              <div
                key={round.id}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
                      <RefreshCcw className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800">{round.name}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          From: {round.sourceWorkloadName}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {round.addressCount} addresses
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(round.createdAt, 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    round.status === 'completed'
                      ? 'bg-accent-100 text-accent-700'
                      : round.status === 'active'
                        ? 'bg-secondary-100 text-secondary-700'
                        : 'bg-gray-100 text-gray-700'
                  }`}>
                    {round.status.charAt(0).toUpperCase() + round.status.slice(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Follow-Up Round Modal */}
      {showCreateModal && (
        <CreateFollowUpModal
          projectId={selectedProject.id}
          workloads={workloads}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

// Create Follow-Up Modal
interface CreateFollowUpModalProps {
  projectId: string;
  workloads: Workload[];
  onClose: () => void;
  onCreated: () => void;
}

const CreateFollowUpModal: React.FC<CreateFollowUpModalProps> = ({
  projectId,
  workloads,
  onClose,
  onCreated
}) => {
  const { currentUser, userProfile } = useAuth();
  const [name, setName] = useState('');
  const [sourceWorkloadId, setSourceWorkloadId] = useState('');
  const [daysSinceLastVisit, setDaysSinceLastVisit] = useState('7');
  const [maxAttempts, setMaxAttempts] = useState('3');
  const [excludeConverted, setExcludeConverted] = useState(true);
  const [selectedDispositions, setSelectedDispositions] = useState<string[]>(['not_home']);
  const [submitting, setSubmitting] = useState(false);

  const dispositionOptions = [
    { id: 'not_home', label: 'Not Home', description: 'Addresses where no one answered' },
    { id: 'refused', label: 'Refused', description: 'Initial refusals to retry' },
    { id: 'callback', label: 'Callback Requested', description: 'Requested to come back later' },
    { id: 'inaccessible', label: 'Inaccessible', description: 'Could not access on first try' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceWorkloadId || !currentUser || !userProfile) return;

    setSubmitting(true);
    try {
      const sourceWorkload = workloads.find(w => w.id === sourceWorkloadId);
      if (!sourceWorkload) throw new Error('Source workload not found');

      // In a real implementation, this would:
      // 1. Query the addresses from the source workload with the selected filters
      // 2. Create a new workload with those filtered addresses
      // 3. Store the follow-up round metadata

      // For now, we'll create a new workload with the same territory
      await workloadService.createWorkload({
        name: name || `Follow-Up: ${sourceWorkload.name}`,
        projectId,
        type: sourceWorkload.type,
        territory: sourceWorkload.territory,
        sourceMetadata: {
          type: 'csv_upload',
          originalFileName: `follow_up_from_${sourceWorkload.id}`,
          uploadedAt: new Date() as any,
          uploadedBy: currentUser.uid,
          totalRows: 0,
          validRows: 0,
          invalidRows: 0,
          mappingConfig: {
            streetColumn: 'street',
            cityColumn: 'city',
            stateColumn: 'state',
            zipColumn: 'zip'
          }
        }
      }, currentUser.uid, userProfile.displayName);

      toast.success('Follow-up round created successfully');
      onCreated();
    } catch (error) {
      console.error('Error creating follow-up round:', error);
      toast.error('Failed to create follow-up round');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">Create Follow-Up Round</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Workload</label>
            <select
              value={sourceWorkloadId}
              onChange={(e) => setSourceWorkloadId(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a workload</option>
              {workloads.map(workload => (
                <option key={workload.id} value={workload.id}>
                  {workload.name} ({workload.progress.totalAddresses} addresses)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Round Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Leave blank to auto-generate"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Include Dispositions</label>
            <div className="space-y-2">
              {dispositionOptions.map(option => (
                <label
                  key={option.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedDispositions.includes(option.id)
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedDispositions.includes(option.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedDispositions([...selectedDispositions, option.id]);
                      } else {
                        setSelectedDispositions(selectedDispositions.filter(d => d !== option.id));
                      }
                    }}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium text-gray-800">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Days Since Last Visit</label>
              <input
                type="number"
                value={daysSinceLastVisit}
                onChange={(e) => setDaysSinceLastVisit(e.target.value)}
                min="1"
                max="365"
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum days since last visit</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Previous Attempts</label>
              <input
                type="number"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                min="1"
                max="10"
                className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">Exclude addresses with more attempts</p>
            </div>
          </div>

          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={excludeConverted}
              onChange={(e) => setExcludeConverted(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <div className="font-medium text-gray-800">Exclude converted leads</div>
              <div className="text-sm text-gray-500">Don't include addresses that already converted to leads</div>
            </div>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !sourceWorkloadId || selectedDispositions.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Follow-Up
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FollowUpRoundsPage;
